import React, { useMemo, useState } from "react";
import "./QBankFilter.css";
import { useQuestionBankFilterList } from "../hooks/useQuestionBankFilterList";

function buildInitialFilters(groups = []) {
  return groups.reduce((filters, group) => {
    filters[group.id] = {
      label: group.defaultValue || "All",
      value: "",
    };
    return filters;
  }, {});
}

function buildShortcutFilters(appliedFilters = {}) {
  return {
    bookmark_status: {
      label: Number(appliedFilters?.bookmark_status || 0) === 1 ? "Bookmarked" : "All",
      value: Number(appliedFilters?.bookmark_status || 0) === 1 ? 1 : "",
    },
    report_status: {
      label: Number(appliedFilters?.report_status || 0) === 1 ? "Reported" : "All",
      value: Number(appliedFilters?.report_status || 0) === 1 ? 1 : "",
    },
  };
}

function buildAppliedFilterState(groups = [], appliedFilters = {}) {
  const initialFilters = buildInitialFilters(groups);

  Object.entries(appliedFilters || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    if (key === "bookmark_status") {
      initialFilters[key] = {
        label: Number(value) === 1 ? "Bookmarked" : "All",
        value: Number(value) === 1 ? 1 : "",
      };
      return;
    }

    if (key === "report_status") {
      initialFilters[key] = {
        label: Number(value) === 1 ? "Reported" : "All",
        value: Number(value) === 1 ? 1 : "",
      };
      return;
    }

    const matchingGroup = groups.find((group) => group.id === key);
    const matchingOption = matchingGroup?.options?.find(
      (option) => String(option?.value) === String(value)
    );

    if (matchingOption) {
      initialFilters[key] = {
        label: matchingOption.label,
        value: matchingOption.value,
      };
    }
  });

  return initialFilters;
}

function toApiFilters(selectedFilters = {}) {
  return Object.entries(selectedFilters).reduce((accumulator, [key, option]) => {
    accumulator[key] = option?.value ?? "";
    return accumulator;
  }, {});
}

const QBankFilter = ({
  onClose,
  onApply,
  className = "",
  queryParams = {},
  appliedFilters = {},
}) => {
  const {
    data: filterData,
    isLoading,
    isError,
    error,
  } = useQuestionBankFilterList(queryParams);
  const filterGroups = useMemo(() => filterData?.groups || [], [filterData?.groups]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({});

  React.useEffect(() => {
    if (!filterGroups.length) {
      return;
    }

    setSelectedFilters(buildAppliedFilterState(filterGroups, appliedFilters));

    setOpenDropdown((current) => current || filterGroups[0]?.id || null);
  }, [appliedFilters, filterGroups]);

  const handleDropdownToggle = (groupId) => {
    setOpenDropdown((current) =>
      current === groupId ? null : groupId
    );
  };

  const handleOptionSelect = (groupId, option) => {
    setSelectedFilters((current) => ({
      ...current,
      [groupId]: option,
    }));
  };

  const handleReset = () => {
    const resetFilters = {
      ...buildInitialFilters(filterGroups),
      ...buildShortcutFilters({}),
    };

    setSelectedFilters(resetFilters);
    setOpenDropdown(filterGroups[0]?.id || null);

    if (onApply) {
      onApply(toApiFilters(resetFilters));
    }

    if (onClose) {
      onClose();
    }
  };

  const handleApply = () => {
    if (onApply) {
      onApply(toApiFilters(selectedFilters));
    }
  };

  const handleShortcutToggle = (key, label) => {
    setSelectedFilters((current) => {
      const isActive = Number(current?.[key]?.value || 0) === 1;
      const nextFilters = {
        ...current,
        [key]: {
          label: isActive ? "All" : label,
          value: isActive ? "" : 1,
        },
      };

      if (onApply) {
        onApply(toApiFilters(nextFilters));
      }

      return nextFilters;
    });
  };

  const isBookmarkedActive = Number(selectedFilters?.bookmark_status?.value || 0) === 1;
  const isReportedActive = Number(selectedFilters?.report_status?.value || 0) === 1;

  return (
    <aside className={`qbf filterBottom ${className}`.trim()}>
      <div className="qbf__header">
        <div className="d-flex align-items-center gap-2">
          <div className="filter-card__icon">
             <i className="bi bi-sliders2"></i>
          </div>
          <div>
          <h2 className="qbf__title">Filters</h2>
          <p className="qbf__subtitle">Refine your results</p>
        </div>
        </div>

        <button
          type="button"
          className="qbf__close  d-flex d-xl-none"
          onClick={onClose}
          aria-label="Close filters"
        >
          <i className="bi bi-x-lg" />
        </button>
      </div>

     <div className="qbf__chipRow" aria-label="Filter shortcuts">
        <button
          type="button"
          className={`qbf__chip${isBookmarkedActive ? " is-active" : ""}`}
          onClick={() => handleShortcutToggle("bookmark_status", "Bookmarked")}
        >
          <i className="bi bi-bookmark" aria-hidden="true" />
          Bookmarked
        </button>
        <button
          type="button"
          className={`qbf__chip${isReportedActive ? " is-active" : ""}`}
          onClick={() => handleShortcutToggle("report_status", "Reported")}
        >
          <i className="bi bi-exclamation-circle" aria-hidden="true" />
          Reported
        </button>
      </div>
      <div className="qbf__groups">
        {isLoading ? (
          <div className="choose-state__status">Loading filters...</div>
        ) : null}
        {!isLoading && isError ? (
          <div className="choose-state__status choose-state__status--error">
            {error?.response?.data?.message || error?.message || "Unable to load filters."}
          </div>
        ) : null}
        {!isLoading && !isError && !filterGroups.length ? (
          <div className="choose-state__status">No filters available.</div>
        ) : null}
        {filterGroups.map((group) => {
          const isOpen = openDropdown === group.id;
          const selectedValue = selectedFilters[group.id]?.label || group.defaultValue || "All";

          return (
            <div
              key={group.id}
              className={`qbf__groupWrap ${
                isOpen ? "is-open" : ""
              }`}
            >
              <button
                type="button"
                className="qbf__group"
                onClick={() =>
                  handleDropdownToggle(group.id)
                }
                aria-expanded={isOpen}
              >
                <span className="qbf__groupIcon">
                  <i className={group.icon} />
                </span>

                <span className="qbf__groupCopy">
                  <strong>{group.title}</strong>
                  <span>{selectedValue}</span>
                </span>

                <i
                  className={`bi ${
                    isOpen
                      ? "bi-chevron-up"
                      : "bi-chevron-down"
                  } qbf__chevron`}
                />
              </button>

              {isOpen && (
                <div className="qbf__dropdown">
                  {group.options.map((option) => {
                    const optionLabel = option?.label || option?.value || "Option";
                    const isSelected = selectedFilters[group.id]?.value === option?.value;

                    return (
                      <button
                        type="button"
                        key={option?.id || optionLabel}
                        className={`qbf__option ${
                          isSelected ? "is-active" : ""
                        }`}
                        onClick={() =>
                          handleOptionSelect(
                            group.id,
                            {
                              label: optionLabel,
                              value: option?.value ?? optionLabel,
                            }
                          )
                        }
                      >
                        <span>{optionLabel}</span>

                        {/* {isSelected && (
                          <i className="bi bi-check2" />
                        )} */}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="qbf__actions flex-footer">
        <button
          type="button"
          className="qbf__apply"
          onClick={handleApply}
        >
          <i className="bi bi-funnel" />
          Apply Filters
        </button>

        <button
          type="button"
          className="qbf__reset"
          onClick={handleReset}
        >
          <i className="bi bi-arrow-clockwise" />
          Reset Filters
        </button>
      </div>
    </aside>
  );
};

export default QBankFilter;
